import type { ModuleKey } from './biModuleRegistry';
import { BI_MODULE_KEYS as ORDERED_KEYS, SHELL_MODULE_REGISTRY } from './biModuleRegistry';
import { GRANULAR_MODULE_KEYS } from './granularModuleKeys';

/** Same as {@link ORDERED_KEYS} — ordered by numeric module code */
export const BI_MODULE_KEYS = ORDERED_KEYS;

/**
 * Skriveni podmoduli po roditelju (hiddenFromNav + parentKey iz registryja).
 * Npr. warehouse → [40.1, 40.2, 40.3, 40.4, …], monestiq → [20.1, 20.2].
 */
const HIDDEN_CHILDREN_BY_PARENT: Partial<Record<ModuleKey, ModuleKey[]>> = (() => {
    const map: Partial<Record<ModuleKey, ModuleKey[]>> = {};
    for (const e of SHELL_MODULE_REGISTRY) {
        if (e.hiddenFromNav && e.parentKey) {
            (map[e.parentKey] ??= []).push(e.key);
        }
    }
    return map;
})();

/**
 * Direktan ACL grant — bez parent-fallbacka i bez always-true specijala (gap/pentest/sops).
 * `allowed_modules === undefined` = superadmin/all-access.
 */
export function hasModuleGrant(user: { allowed_modules?: string[] }, key: ModuleKey): boolean {
    return user.allowed_modules === undefined || user.allowed_modules.includes(key);
}

/** Skriveni podmoduli roditelja na koje user ima DIREKTAN grant (za filtriranje landing kartica). */
export function grantedHiddenChildren(user: { allowed_modules?: string[] }, parent: ModuleKey): ModuleKey[] {
    const kids = HIDDEN_CHILDREN_BY_PARENT[parent] ?? [];
    return kids.filter((k) => hasModuleGrant(user, k));
}

export function canOpenModule(user: { allowed_modules?: string[] }, key: ModuleKey): boolean {
    const allowed = user.allowed_modules;
    if (allowed === undefined) {
        return true;
    }
    // Interni imenik je dostupan SVIM ulogiranim korisnicima — bez ACL provjere.
    if (key === 'directory') {
        return true;
    }
    // m.52 SOP Akademija — dostupna svima; stvarni gate je per-SOP dodjela (backend ACL).
    if (key === 'sops') {
        return true;
    }
    // Pentest Monitor — owner-only; stvarni gate je email (render + backend), ovdje samo ne blokiraj nav-redirect.
    if (key === 'pentest_monitor') {
        return true;
    }
    // GAP izvještaj / auto-izračun (40.5.1 / 40.5.2) — owner-only; gate je email (render + backend).
    if (key === 'warehouse_gap_report' || key === 'warehouse_gap_auto') {
        return true;
    }
    // m.40.43 Preskladištenje, priprema: vide ga svi koji vide neki 40.x (Vedran 21.10.2026); backend gate je isti.
    // m.40.44 Dopuna lagera, priprema (UID 990): isti krug kao 40.43.
    // m.40.45-40.47 (UID 1018): isti krug, plus izravna dodjela modula.
    if (key === 'warehouse_restock_prep' || key === 'warehouse_branch_restock' || key === 'warehouse_import_orders' || key === 'warehouse_tca_invoices' || key === 'warehouse_gtm_scrap') {
        return allowed.some((k) => k === 'warehouse' || k.startsWith('warehouse_'));
    }
    // m.21.5 Plaćanja & Cash-Flow — wrapper nad 4 faze; otvori ako korisnik ima pristup BILO KOJOJ fazi.
    if (key === 'platni_cf') {
        return ['payment_selection', 'payment_batches', 'contracts', 'buyback_payout'].some((k) => allowed.includes(k));
    }
    // m.40.63 Pokrivenost zlata V3 — isti krug kao 40.61 (backend: GoldCoverageV2Controller::assertCanView v3).
    if (key === 'warehouse_coverage_v3') {
        return allowed.includes('warehouse_coverage_v3') || allowed.includes('warehouse_coverage_v2');
    }
    // m.21.6 Sravnavanje blagajni — backend gate je coarse 'payment_traffic' (isti ljudi kao Platni promet).
    if (key === 'payment_reconciliation') {
        return allowed.includes('payment_traffic') || allowed.includes('payment_reconciliation');
    }
    if (key === 'admin') {
        if (allowed.includes('admin')) {
            return true;
        }
        if (allowed.includes('m.100')) {
            return true;
        }

        return GRANULAR_MODULE_KEYS.some((g) => allowed.includes(g));
    }

    if (allowed.includes(key)) {
        return true;
    }

    // Roditelj sa SKRIVENIM podmodulom na koji user ima grant → roditelj vidljiv u navu
    // (landing roditelja prikazuje samo dozvoljene kartice; vidi grantedHiddenChildren).
    // Npr. user samo s 40.3 Skladište vidi modul 40. RAW includes (ne canOpenModule) da
    // always-true specijali (gap/pentest) ne otvore roditelja svima.
    return (HIDDEN_CHILDREN_BY_PARENT[key] ?? []).some((k) => allowed.includes(k));
}
